import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { postModuleAttempt, uploadRecitation, submitForPeerReview } from "@/lib/apiClient";
import { checkAchievements } from "@/lib/achievements";
import { pickMime } from "@/lib/audioRecording";
import ModuleFeedbackPrompt from "@/components/ModuleFeedbackPrompt";
import PeerReviewTargetPicker from "@/components/PeerReviewTargetPicker";
import {
  Play, Square, RotateCcw, CheckCircle2, XCircle, Mic, AlertTriangle, Users, Lock,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { renderVerseText, newWordsInAyah } from "./recordingRender";

// A recorded section must cover at least this fraction of its Maximum time
// to count as a genuine read-through — mirrors backend/routes/modules.py.
const MIN_ATTEMPT_RATIO = 0.75;

/**
 * Test content for module 9 — mirrors TestSelfAssessment.jsx exactly: the
 * section to test comes from the shared picker above; pressing Test starts
 * recording + timer together; stop() grades immediately from elapsed time
 * (not gated on the recording blob); on pass, a post-pass dialog offers
 * Continue or Submit to peer review — recording is only uploaded at that
 * point, never automatically. No Ayah/New-Words playback, no Tajweed
 * toggle — those are Learn-only affordances; Test shows only the ayah
 * text (still with its inherent new-word highlighting) plus the standard
 * font-size control every other module's Test screen has.
 */
export default function RecordingMode({ module: m, progress, onAttemptSaved, onRunningChange, hasSections, selectedSection, sectionUnlocked }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("idle"); // idle | running | done
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [fontStep, setFontStep] = useState(() => {
    const v = Number(localStorage.getItem("qf:font-step"));
    return Number.isFinite(v) && v >= -1 && v <= 2 ? v : 0;
  });
  const [recBlob, setRecBlob] = useState(null);
  const [recUrl, setRecUrl] = useState(null);
  const [recError, setRecError] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [peerSubmitting, setPeerSubmitting] = useState(false);
  const [peerSubmitted, setPeerSubmitted] = useState(false);
  const [peerTarget, setPeerTarget] = useState({ target_type: null, target_circle_id: null, ready: false });
  const mediaRecorder = useRef(null);
  const recChunks = useRef([]);
  const recStream = useRef(null);
  const startedAt = useRef(null);
  const ticker = useRef(null);

  const allAyahs = (m.passages || []).flatMap((p) => (p.ayahs || []).map((a) => ({ ...a, passage: p })));
  const sectionAyahs = hasSections
    ? allAyahs.filter((a) => (a.rule || String(a.n)) === selectedSection)
    : allAyahs;
  const isGroup = sectionAyahs.length > 1;
  const passage = sectionAyahs[0]?.passage;
  const isFirstAyahOfPassage = passage?.ayahs?.[0]?.n === sectionAyahs[0]?.n;
  const sectionTargetSec = sectionAyahs.reduce((sum, a) => sum + (a.target_seconds || 0), 0);
  const sectionExpertSec = sectionAyahs.reduce((sum, a) => sum + (a.expert_seconds || 0), 0);
  const sectionArText = sectionAyahs.map((a) => a.ar).join(" ");
  const sizeClass = ["size-sm", "size-sm", "size-md", "size-lg"][fontStep + 1] || "size-md";

  useEffect(() => localStorage.setItem("qf:font-step", String(fontStep)), [fontStep]);
  useEffect(() => { onRunningChange && onRunningChange(mode === "running"); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    if (recUrl) URL.revokeObjectURL(recUrl);
    if (recStream.current) recStream.current.getTracks().forEach((tr) => tr.stop());
    if (ticker.current) clearInterval(ticker.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    clearInterval(ticker.current);
    setMode("idle"); setElapsed(0); setResult(null);
    setRecBlob(null); setRecError(null);
    if (recUrl) { URL.revokeObjectURL(recUrl); setRecUrl(null); }
    setSubmitted(null); setPeerSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSection]);

  if (sectionAyahs.length === 0) return null;

  const start = async () => {
    setMode("running"); setResult(null); setElapsed(0);
    setRecBlob(null); setRecError(null);
    if (recUrl) { URL.revokeObjectURL(recUrl); setRecUrl(null); }
    setSubmitted(null); setPeerSubmitted(false);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setRecError(t("rd.err_unsupported"));
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        recStream.current = stream;
        const recMime = pickMime();
        const rec = new MediaRecorder(stream, { mimeType: recMime });
        recChunks.current = [];
        rec.ondataavailable = (e) => e.data.size && recChunks.current.push(e.data);
        rec.onstop = () => {
          stream.getTracks().forEach((tr) => tr.stop());
          if (!recChunks.current.length) { setRecError(t("rd.err_empty")); return; }
          const blobType = rec.mimeType || recMime || recChunks.current[0]?.type || "audio/webm";
          const b = new Blob(recChunks.current, { type: blobType });
          if (b.size < 256) { setRecError(t("rd.err_tiny")); return; }
          setRecBlob(b);
          setRecUrl(URL.createObjectURL(b));
        };
        mediaRecorder.current = rec;
        rec.start();
      } catch (e) {
        mediaRecorder.current = null;
        const name = e?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") setRecError(t("rd.err_blocked"));
        else if (name === "NotFoundError" || name === "DevicesNotFoundError") setRecError(t("rd.err_no_mic"));
        else setRecError(t("rd.err_generic"));
      }
    }
    startedAt.current = Date.now();
    ticker.current = setInterval(() => setElapsed(Date.now() - startedAt.current), 100);
  };

  const stop = async () => {
    clearInterval(ticker.current);
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      try { mediaRecorder.current.stop(); } catch (_e) { /* harmless */ }
    }
    const ms = Date.now() - startedAt.current;
    setElapsed(ms);
    setMode("done");

    try {
      const res = await postModuleAttempt(m.code, {
        module_code: m.code,
        elapsed_ms: ms,
        section: selectedSection,
      });
      setResult(res);
      onAttemptSaved && onAttemptSaved();
      if (res.attempt.passed) {
        checkAchievements();
        toast.success(t("rd.toast_subset_pass", { sec: selectedSection, s: (ms / 1000).toFixed(1) }));
      } else if (res.attempt.fail_reason === "too_fast") {
        toast.error(t("rd.toast_subset_miss_too_fast", { sec: selectedSection, min: Math.ceil(sectionTargetSec * MIN_ATTEMPT_RATIO) }));
      } else {
        toast.error(t("rd.toast_subset_miss", { sec: selectedSection, target: sectionTargetSec }));
      }
    } catch (_e) {
      toast.error(t("rd.toast_save_failed"));
    }
  };

  const reset = () => {
    clearInterval(ticker.current);
    setMode("idle"); setElapsed(0); setResult(null);
    setRecBlob(null); setRecError(null);
    if (recUrl) { URL.revokeObjectURL(recUrl); setRecUrl(null); }
    setSubmitted(null); setPeerSubmitted(false);
  };

  const submitToPeerReview = async () => {
    if (!recBlob || peerSubmitting || peerSubmitted || !peerTarget.ready) return;
    setPeerSubmitting(true);
    try {
      const note = t("rd.submit_note", { n: m.n, subset: ` · ${selectedSection}` });
      const uploaded = submitted?.recitation_id
        ? submitted
        : await uploadRecitation(recBlob, `${m.code}-recording.webm`, note, m.code, selectedSection, sectionArText);
      setSubmitted(uploaded);
      await submitForPeerReview({
        recitation_id: uploaded.recitation_id,
        module_code: m.code,
        section: selectedSection,
        note,
        target_type: peerTarget.target_type,
        target_circle_id: peerTarget.target_circle_id,
      });
      setPeerSubmitted(true);
      toast.success(t("rec.toast_peer_open"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 409) toast.error(detail || t("rd.postpass_peer_already"));
      else toast.error(detail || t("rec.toast_peer_failed"));
    } finally { setPeerSubmitting(false); }
  };

  const secs = (elapsed / 1000).toFixed(1);
  const targetMs = sectionTargetSec * 1000;
  const pct = Math.min(100, Math.round(100 * elapsed / Math.max(targetMs, 1)));

  return (
    <>
      {hasSections && !sectionUnlocked && (
        <div data-testid="section-locked-hint" className="mb-4 flex items-center gap-2 text-xs text-[#B45309] bg-[#FFF7ED] border border-[#FED7AA] rounded-xl px-3 py-2">
          <Lock className="w-3.5 h-3.5 flex-shrink-0" /> {t("rd.section_locked_message")}
        </div>
      )}

      <div className="rounded-3xl bg-[#064E3B] text-[#FDFBF7] p-7 sm:p-9 relative overflow-hidden" data-testid="recording-mode">
        <div className="absolute inset-0 pattern-islamic opacity-25" />
        <div className="relative">
          <div className="sticky sticky-below-header z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-2 bg-[#064E3B]/95 backdrop-blur" data-testid="sticky-toolbar">
            <div className="rounded-2xl bg-white/10 border border-white/15 p-3">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  {mode !== "running" && (
                    <div className="inline-flex items-center rounded-full border border-[#FED7AA]/40 overflow-hidden" data-testid="font-size-control">
                      <button data-testid="font-smaller" onClick={() => setFontStep((s) => Math.max(-1, s - 1))}
                        className="px-2.5 py-2 text-sm text-[#FED7AA] hover:bg-white/10 transition" aria-label={t("rd.smaller")}>A−</button>
                      <span className="px-1.5 text-[10px] text-[#FED7AA]/70 tabular-nums">{t("rd.font_size_label")}</span>
                      <button data-testid="font-larger" onClick={() => setFontStep((s) => Math.min(2, s + 1))}
                        className="px-2.5 py-2 text-sm text-[#FED7AA] hover:bg-white/10 transition" aria-label={t("rd.larger")}>A+</button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2" data-testid="timer-area">
                  {mode === "running" && !recError && (
                    <span data-testid="rec-active-chip" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FEF2F2] text-[#EF4444] text-[11px] font-semibold border border-[#FECACA]">
                      <span className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse" /> {t("rd.rec")}
                    </span>
                  )}
                  {mode === "running" && recError && (
                    <span data-testid="rec-fail-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-[#FED7AA] text-[10px] font-medium" title={recError}>
                      <Mic className="w-3 h-3" /> {t("rd.mic_off")}
                    </span>
                  )}
                  {sectionExpertSec > 0 && (
                    <span data-testid="timer-min-chip" className="inline-flex flex-col items-center px-2 py-1 rounded-lg bg-[#FEF2F2] border border-[#EF4444]/40 leading-none">
                      <span className="text-[8px] uppercase tracking-wider text-[#EF4444] font-bold">Min</span>
                      <span className="text-xs font-semibold text-[#B91C1C] tabular-nums">{sectionExpertSec}s</span>
                    </span>
                  )}
                  <span data-testid="m9-timer" className="font-heading text-2xl sm:text-3xl font-light tabular-nums text-white">
                    {secs}<span className="text-sm text-[#FDFBF7]/70">s</span>
                  </span>
                  {sectionTargetSec > 0 && (
                    <span data-testid="timer-max-chip" className="inline-flex flex-col items-center px-2 py-1 rounded-lg bg-[#FEF2F2] border border-[#EF4444]/40 leading-none">
                      <span className="text-[8px] uppercase tracking-wider text-[#EF4444] font-bold">Max</span>
                      <span className="text-xs font-semibold text-[#B91C1C] tabular-nums">{sectionTargetSec}s</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {mode !== "running" ? (
                    sectionUnlocked ? (
                      <button data-testid="start-btn" onClick={start}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white font-medium transition shadow-sm">
                        <Play className="w-4 h-4" /> {t("rd.test")}
                      </button>
                    ) : (
                      <span data-testid="start-btn-locked" title={t("rd.section_locked_message")}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 text-[#FED7AA]/60 font-medium cursor-not-allowed">
                        <Lock className="w-4 h-4" /> {t("rd.section_locked_title")}
                      </span>
                    )
                  ) : (
                    <button data-testid="stop-btn" onClick={stop}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white font-medium transition shadow-sm recording-ring">
                      <Square className="w-4 h-4" /> {t("rd.stop_test")}
                    </button>
                  )}
                  {(mode === "done" || mode === "running") && (
                    <button onClick={reset} className="inline-flex items-center gap-2 px-3 py-2.5 rounded-full bg-white/10 border border-white/15 hover:border-white/30 text-[#FED7AA] transition" title={t("rd.reset_title")}>
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {sectionTargetSec > 0 && (mode === "running" || mode === "done") && (
                <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: pct > 100 ? "#EF4444" : "#D97706" }} />
                </div>
              )}

              {mode === "done" && (recUrl || recError) && (
                <div className="mt-3 pt-3 border-t border-white/15" data-testid="recording-controls">
                  {recUrl ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#FED7AA] font-semibold">
                        <Mic className="w-3 h-3" /> {t("rd.your_recording")}
                      </span>
                      <audio data-testid="recording-audio" controls preload="metadata" src={recUrl} className="h-9 max-w-xs">
                        {t("rd.audio_unsupported")}
                      </audio>
                    </div>
                  ) : (
                    <div data-testid="recording-error" className="flex items-start gap-2 text-xs text-[#FED7AA] bg-white/10 border border-white/15 rounded-xl px-3 py-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{recError}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Scrollable independently of the page so a long passage doesn't
              push the record/stop controls out of view — the sticky
              toolbar above stays outside this container. */}
          <div className="mt-6 max-h-[50vh] sm:max-h-[55vh] overflow-y-auto pr-1" data-testid="passage-scroll-area">
            {isFirstAyahOfPassage && passage?.prelude && (
              <div className={`font-madani ${sizeClass === "size-lg" ? "size-md" : "size-sm"} text-right text-[#FED7AA]/90`}>
                {passage.prelude}
              </div>
            )}
            <div className="space-y-4" data-testid="passage-ayahs">
              {sectionAyahs.map((a) => (
                <div key={a.n} className="rounded-2xl bg-white/5 border border-white/10 p-3 sm:p-4">
                  {isGroup && <div className="text-[10px] font-mono text-[#FED7AA]/60 mb-1.5" dir="ltr">{t("rec.ayah_n", { n: a.n })}</div>}
                  <div className={`font-madani ${sizeClass} text-right`} dir="rtl">
                    {renderVerseText(a.ar, newWordsInAyah(a), false)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {mode === "done" && result && (
          <div data-testid="result-indicator" className={`rounded-2xl p-5 border-2 ${result.attempt.passed ? "bg-[#ECFDF5] border-[#10B981]" : "bg-[#FEF2F2] border-[#EF4444]"}`}>
            <div className="flex items-start gap-3 flex-wrap">
              {result.attempt.passed ? <CheckCircle2 className="w-8 h-8 text-[#10B981] flex-shrink-0" /> : <XCircle className="w-8 h-8 text-[#EF4444] flex-shrink-0" />}
              <div className="flex-1 min-w-[200px]">
                <div className="font-heading text-xl font-semibold text-[#1F2937]" data-testid="result-headline">
                  {result.attempt.passed ? t("rd.result_passed") : t("rd.result_failed")}
                </div>
                <div className="mt-1 text-sm text-[#1F2937]" data-testid="result-sub-message">
                  {result.attempt.passed
                    ? t("rd.result_passed_message")
                    : result.attempt.fail_reason === "too_fast"
                      ? t("rd.result_failed_message_too_fast", { min: Math.ceil(sectionTargetSec * MIN_ATTEMPT_RATIO) })
                      : t("rd.result_failed_message", { target: sectionTargetSec })}
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm">
                  <div data-testid="result-your-time" className="text-[#1F2937]">{t("rd.result_your_time", { s: (elapsed / 1000).toFixed(1) })}</div>
                  <div data-testid="result-target-time" className="text-[#1F2937]">{t("rd.result_target_time", { target: sectionTargetSec })}</div>
                  {sectionTargetSec > 0 && (
                    <div data-testid="result-min-time" className="text-[#1F2937]">{t("rd.result_min_time", { min: Math.ceil(sectionTargetSec * MIN_ATTEMPT_RATIO) })}</div>
                  )}
                  {sectionExpertSec > 0 && (
                    <div data-testid="result-expert-time" className="text-[#D97706]">{t("rd.result_expert_time", { expert: sectionExpertSec })}</div>
                  )}
                </div>

                {progress?.sections?.[selectedSection] && (
                  <div data-testid="previous-stats" className="mt-3 flex flex-wrap items-center gap-2">
                    {progress.sections[selectedSection].best_ms && (
                      <span data-testid="previous-best" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#A7F3D0] text-[11px] font-medium text-[#1F2937]">
                        {t("rd.result_best_label")}: {(progress.sections[selectedSection].best_ms / 1000).toFixed(1)}s
                      </span>
                    )}
                    {progress.sections[selectedSection].attempts > 0 && (
                      <span data-testid="previous-attempts" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#A7F3D0] text-[11px] font-medium text-[#1F2937]">
                        {t("rd.result_attempts_label")}: {progress.sections[selectedSection].attempts}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {!result.attempt.passed && (
                <button data-testid="result-retry-btn" onClick={reset}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-sm font-medium transition">
                  <RotateCcw className="w-4 h-4" /> {t("rd.result_retry")}
                </button>
              )}
            </div>

            {result.attempt.passed && recBlob && !peerSubmitted && (
              <div className="mt-5 pt-4 border-t border-current/20" data-testid="post-pass-dialog">
                <div className="text-sm text-[#1F2937] font-medium">{t("rd.postpass_title")}</div>
                <div className="text-xs text-[#4B5563] mt-1">{t("rd.postpass_body")}</div>
                <div className="mt-3">
                  <PeerReviewTargetPicker onChange={setPeerTarget} />
                </div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <Link data-testid="postpass-continue" to="/dashboard"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] text-sm font-medium transition">
                    {t("rd.postpass_continue")} →
                  </Link>
                  <button data-testid="postpass-peer-review" onClick={submitToPeerReview}
                    disabled={peerSubmitting || !peerTarget.ready}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-medium transition disabled:opacity-60 disabled:cursor-not-allowed">
                    <Users className="w-4 h-4" /> {peerSubmitting ? t("rec.peer_submitting") : t("rd.postpass_peer_send")}
                  </button>
                </div>
              </div>
            )}
            {result.attempt.passed && recBlob && peerSubmitted && (
              <div className="mt-5 pt-4 border-t border-current/20 flex items-center gap-2 flex-wrap" data-testid="post-pass-submitted">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm text-[#1F2937]">{t("rec.peer_submitted")}</span>
                <Link to="/dashboard" className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
                  {t("rd.postpass_continue")} →
                </Link>
              </div>
            )}

            <ModuleFeedbackPrompt key={result.attempt.created_at || `${m.code}-${elapsed}`} moduleCode={m.code} />
          </div>
        )}
      </div>
    </>
  );
}
